import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { openModal } from "react-url-modal";
import { IGame } from "@/redux/games/types";
import { getTokenFromStorage } from "@/utils";
import { Button, Toast } from "@/components";
import { formatDateTimeRussian } from "@/utils/date/dateGrouping";
import styles from "./GamesItem.module.scss";
import CheckMarkGray from "@/assets/icons/checkMarkGray.svg";
import TechRequest from "@/assets/icons/techRequest.svg";
import shareIcon from "@/assets/icons/share.svg";

interface GamesItemProps {
  item: IGame;
  button?: boolean;
  isMyGames?: boolean;
  isCreateGame?: boolean;
  isAppliedGame?: boolean;
  handleCancelModalVisible?: (id: number) => void;
  setIsRequiredAuthOpened: (value: boolean) => void;
  modalWindow?: boolean;
}

export const GamesItem: React.FC<GamesItemProps> = ({
  item,
  button = true,
  isMyGames = false,
  isCreateGame = false,
  isAppliedGame = false,
  handleCancelModalVisible,
  setIsRequiredAuthOpened,
  modalWindow = false,
}) => {
  const navigate = useNavigate();
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");

  const formatDateTime = (date: string, startTime: string, endTime: string) => {
    return formatDateTimeRussian(date, startTime, endTime);
  };

  const formatPhone = (phone: string) => {
    return phone.replace(/(\d{1})(\d{3})(\d{3})(\d{2})(\d{2})/, '7 ($2) $3-$4-$5');
  };

  const handleCopyPhone = async () => {
    if (!item.creator?.phone) return;
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(item.creator.phone);
        setToastMessage("Телефон скопирован в буфер обмена");
        setShowToast(true);
      }
    } catch (error) {
      console.error("Failed to copy phone:", error);
    }
  };

  const handleJoinGame = () => {
    const token = getTokenFromStorage();
    if (!token) {
      setIsRequiredAuthOpened(true);
      return;
    }

    // Открываем модальное окно вместо навигации
    const params: any = { idGame: item.id.toString() };
    if (isCreateGame) {
      params.isCreateGame = "true";
    } else if (isAppliedGame) {
      params.isAppliedGame = "true";
    }

    openModal({ name: "joinGame", params });
  };

  const unreadApplicationsCount =
    item.applications?.filter((app) => !app.is_seen).length || 0;

  const shareLink = `${window.location.origin}/padel/games/${item.id}`;

  const handleShareGame = async () => {
    try {
      if (navigator.share) {
        await navigator.share({
          title: "Игра TennisApp",
          url: shareLink,
        });
        return;
      }
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(shareLink);
      }
    } catch (error) {
      console.error("Share error:", error);
      if (navigator.clipboard && navigator.clipboard.writeText) {
        try {
          await navigator.clipboard.writeText(shareLink);
        } catch (clipError) {
          console.error("Clipboard error:", clipError);
        }
      }
    }
  };

  const renderActions = () => {
    if (isMyGames) {
      return (
        <div className={styles.actions}>
          <Button onClickFunc={handleJoinGame} newStyles={styles.buttonTransparent}>
            Информация об игре
          </Button>
          {handleCancelModalVisible && (
            <Button
              onClickFunc={() => handleCancelModalVisible(item.id)}
              newStyles={styles.buttonTransparent}
            >
              Отменить
            </Button>
          )}
        </div>
      );
    }

    if (isCreateGame) {
      return (
        <div className={styles.actions}>
          <Button onClickFunc={handleJoinGame} newStyles={styles.buttonTransparent}>
            Управлять игрой
          </Button>
        </div>
      );
    }

    if (isAppliedGame) {
      return (
        <div className={styles.actions}>
          <Button onClickFunc={handleJoinGame} newStyles={styles.buttonTransparent}>
            Информация об игре
          </Button>
          {handleCancelModalVisible && (
            <Button
              onClickFunc={() => handleCancelModalVisible(item.id)}
              newStyles={styles.buttonTransparent}
            >
              Отменить заявку
            </Button>
          )}
        </div>
      );
    }

    return (
      <div className={styles.actions}>
        <Button onClickFunc={handleJoinGame} newStyles={styles.button}>
          Оставить заявку
        </Button>
      </div>
    );
  };

  return (
    <div className={`${styles.item} ${modalWindow ? styles.modalWindow : ''}`}>
      <div className={styles.info}>
        <div className={styles.sectionRow}>
          <div className={styles.sectionTitle}>Время</div>
          <div className={styles.pill}>
            <span className={styles.pillText}>
              {formatDateTime(item.date, item.startTime, item.endTime)}
            </span>
          </div>
        </div>

        <div className={styles.sectionRowLevels}>
          <div className={styles.sectionTitle}>Уровень</div>
          <div className={styles.levelItems}>
            {item.level.map((level) => (
              <div key={level} className={styles.pill}>
                <span className={styles.pillText}>{level}</span>
              </div>
            ))}
          </div>
        </div>

        <div className={styles.sectionRow}>
          <div className={styles.sectionTitle}>Свободных мест</div>
          <div className={styles.pill}>
            <span className={styles.pillText}>{item.emptySlots}</span>
          </div>
        </div>

        {item.creator && (
          <div className={styles.sectionRow}>
            <div className={styles.sectionTitle}>Организатор</div>
            <div className={styles.pillOrganizer}>
              <span className={styles.pillText}>{item.creator.name}</span>
              <span
                className={styles.phone}
                onClick={handleCopyPhone}
                style={{ cursor: "pointer" }}
              >
                {formatPhone(item.creator.phone)}
              </span>
            </div>
          </div>
        )}

        {item.comment && (
          <div className={styles.commentRow}>
            <img src={TechRequest} alt="tech request" />
            <div className={styles.comment}>{item.comment}</div>
          </div>
        )}

        {item.isRented && (
          <div className={styles.statusRow}>
            <img src={CheckMarkGray} alt="checkmark gray" />
            <span className={styles.statusText}>Корт забронирован</span>
          </div>
        )}

        {isCreateGame && (
          <div className={styles.detailInfo}>
            <div className={styles.detailLabel}>
              <span className={styles.sectionTitle}>Заявки</span>
            </div>
            <div className={styles.pill}>
              <span className={styles.pillText}>
                {item.applications?.length || 0}
              </span>
            </div>
          </div>
        )}
      </div>
      <div className={styles.footer}>
        <Button onClickFunc={handleShareGame} newStyles={styles.shareButton}>
          Поделиться
          <img src={shareIcon} alt="share" className={styles.shareIcon} />
        </Button>
        {button && renderActions()}
      </div>
      <Toast
        message={toastMessage}
        isVisible={showToast}
        onClose={() => setShowToast(false)}
      />
    </div>
  );
};
